import React, { useEffect, useState } from 'react';
import { isNativeApp, getPlatform } from '../utils/platform';
import { appAPI } from '../utils/api';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

// Compare semver strings: returns -1, 0, or 1
const cmp = (a, b) => {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
};

// Shows an "update available" prompt inside the native app only (no-op on web/PWA).
const UpdatePrompt = () => {
  const [update, setUpdate] = useState(null); // { url, forced }
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isNativeApp()) return;
    let active = true;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const info = await App.getInfo();
        const current = info.version;
        const res = await appAPI.getVersion();
        const { latest_version, min_version, android_url, ios_url } = res.data;
        const url = getPlatform() === 'ios' ? ios_url : android_url;
        if (!active || !url) return;
        if (cmp(current, min_version) < 0) setUpdate({ url, forced: true });
        else if (cmp(current, latest_version) < 0) setUpdate({ url, forced: false });
      } catch {
        /* ignore — never block the app on a version check */
      }
    })();
    return () => { active = false; };
  }, []);

  const openStore = () => {
    if (update?.url) window.open(update.url, '_system');
  };

  if (!update || (dismissed && !update.forced)) return null;

  if (update.forced) {
    return (
      <div className="fixed inset-0 z-[100] bg-[#064E3B]/95 flex items-center justify-center px-6" data-testid="force-update">
        <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
          <div className="w-14 h-14 rounded-2xl bg-[#84CC16] flex items-center justify-center mx-auto mb-4">
            <Download className="w-6 h-6 text-black" />
          </div>
          <h2 className="text-xl font-bold text-[#064E3B] mb-2">Update required</h2>
          <p className="text-sm text-gray-600 mb-6">A new version of MoneyFeed is available. Please update to keep using the app.</p>
          <Button onClick={openStore} className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold py-6 rounded-full" data-testid="force-update-btn">
            Update now
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[90] mx-auto max-w-md bg-[#064E3B] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3" data-testid="update-banner">
      <Download className="w-5 h-5 text-[#84CC16] shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-semibold">Update available</p>
        <p className="text-xs text-[#D1FAE5]">Get the latest MoneyFeed with new features & fixes.</p>
      </div>
      <Button size="sm" onClick={openStore} className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold" data-testid="update-banner-btn">
        Update
      </Button>
      <button onClick={() => setDismissed(true)} className="text-[#D1FAE5] hover:text-white" data-testid="dismiss-update-btn">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default UpdatePrompt;
